import { Component } from '@angular/core';
import { PeriodSequence } from '@models/generic/period-sequence.model';
import { DelegateDto } from '@models/users/delegate.dto';
import { PeriodTimeDto } from '@models/users/period-time.dto';
import { PeriodDto } from '@models/users/period.dto';
import { SubscriberService } from '@services/subscriber.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'ebs-sequence-creator-dialog',
  templateUrl: './sequence-creator-dialog.component.html',
  styleUrls: ['./sequence-creator-dialog.component.scss'],
})
export class SequenceCreatorDialogComponent {
  days = {
    0: 'Lun',
    1: 'Mar',
    2: 'Mer',
    3: 'Gio',
    4: 'Ven',
    5: 'Sab',
    6: 'Dom',
  };

  readonly DAY_TO_MILLISECONDS: number = 24 * 60 * 60 * 1000;

  sequencePeriod: PeriodSequence = {
    days: [],
    delegate: [],
    times: [
      {
        startTime: '08:00',
        endTime: '20:00',
        delegate: '',
      },
    ],
  };

  public frequency!: number;

  rangeDates: (Date | null)[] = [null, null];
  readonly maxSelectableDate: Date = new Date(9999, 11, 31);
  minDate: Date = new Date(this.firstDayOfWeek(new Date()));
  delegates: DelegateDto[] = [];
  targetDelegates: DelegateDto[] = [];

  constructor(
    public ref: DynamicDialogRef,
    private subscriberService: SubscriberService,
  ) {
    this.loadDelegates();
  }

  loadDelegates() {
    this.subscriberService.getDelegates().subscribe((res) => {
      this.delegates = res;
    });
  }

  setStartDate(date: Date | null) {
    this.rangeDates[0] = date ? this.firstDayOfWeek(date) : null;
    if (this.rangeDates[0] && this.rangeDates[1] && this.rangeDates[1] < this.rangeDates[0]) {
      this.rangeDates[1] = null;
    }
  }

  setEndDate(date: Date | null) {
    this.rangeDates[1] = date ? this.lastDayOfWeek(date) : null;
  }

  firstDayOfWeek(generic: Date): Date {
    if (generic.getDay() === 1) {
      return generic;
    }
    let difference = generic.getDay() === 0 ? 6 : generic.getDay() - 1;
    return new Date(generic.getTime() - difference * this.DAY_TO_MILLISECONDS);
  }

  lastDayOfWeek(generic: Date): Date {
    if (generic.getDay() === 0) {
      return generic;
    }
    let difference = 7 - generic.getDay();
    return new Date(generic.getTime() + difference * this.DAY_TO_MILLISECONDS);
  }

  createNewTime(index: number) {
    this.sequencePeriod.times.splice(index + 1, 0, {
      startTime: this.sequencePeriod.times[index].endTime,
      delegate: '',
      endTime: this.sequencePeriod.times[index].endTime,
    });
  }

  generateSequence() {
    let periods: PeriodDto[] = [];

    if (!this.rangeDates[0] || !this.rangeDates[1]) {
      return;
    }

    let last = this.rangeDates[1].getTime();
    let first = this.rangeDates[0].getTime();

    let frequencyValue = this.frequency;
    let weekDay = 0;
    let periodIdx = 0;
    let frequencyTotal = 0;
    for (let time = first; time <= last; time += this.DAY_TO_MILLISECONDS) {
      if (weekDay % 7 === 0) {
        periods[periodIdx] = {
          startDate: new Date(time),
          units: [],
          endDate: new Date(),
        };
      }

      if (frequencyValue === 0) {
        frequencyValue = this.frequency;
        frequencyTotal++;
      }

      let delegate =
        this.targetDelegates[frequencyTotal % this.targetDelegates.length];
      let isIncluded = this.sequencePeriod.days.includes(
        this.dayToItalianNotation(new Date(time).getDay()),
      );
      if (isIncluded) {
        periods[periodIdx].units.push({
          times: [
            ...this.sequencePeriod.times.map((elem) => {
              return <PeriodTimeDto>{
                startTime: elem.startTime,
                endTime: elem.endTime,
                delegate: `${delegate!.user!.name!.toUpperCase()} ${delegate.user!.lastName}`,
              };
            }),
          ],
          unitDate: new Date(time),
          isActive: true,
        });
        frequencyValue--;
      } else {
        periods[periodIdx].units.push({
          times: [],
          unitDate: new Date(time),
          isActive: false,
        });
      }

      if (weekDay % 7 === 6) {
        periods[periodIdx].endDate = new Date(time);
        periodIdx++;
      }

      weekDay++;
    }
    this.ref.close(periods);
  }

  dayToItalianNotation(day: number) {
    return day === 0 ? 6 : day - 1;
  }
}

export interface Frequency {
  days: number;
  label: string;
}
